import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { VEHICLE_IMAGES } from "../../../../../Utils/Vehicleimg";
import useCustomerDetails from "../../../../Services/Customer/useCustomerDetails";
import { GlobalContext } from "../../../../../Context/GlobalContext";
import useGoogleLocationApi from "../../../../Services/Customer/useGoogleLocationApi";
import VehicleFareLeftPanel from "./VehicleFareLeftPanel";
import VehicleRecommendationPanel from "./VehicleRecommendationPanel";

const VehicleFareCard = ({
  onNext,
  onBack,
  selectedVehicle,
  setSelectedVehicle,
  formData,
  setFormData,
  setSelectedFare,
  setSelectedVehicleMeta,
}) => {
  const { car, truck, auto, bike } = VEHICLE_IMAGES;
  const { userCode, userID } = useContext(GlobalContext);
  const {
    getFaresApi,
    getFaresLoading,
    bookParcelApi,
    getAllVehicleDetailsApi,
  } = useCustomerDetails();

  const { computeDistanceRouteApi, computeDistanceRouteLoading } =
    useGoogleLocationApi();

  const [fareData, setFareData] = useState({
    twoWheelerFare: {},
    threeWheelerFare: {},
    fourWheelerFare: {},
    miniTruckFare: {},
  });

  const [activeVehicle, setActiveVehicle] = useState("Two Wheeler");
  const [showPayment, setShowPayment] = useState(false);

  const [payAt, setPayAt] = useState("Pick Up");

  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropMarkerRef = useRef(null);
  const routeLineRef = useRef(null);

  const pickupLocation = useMemo(() => {
    return {
      lat: Number(formData?.pickupLatitude || 28.6139),
      lng: Number(formData?.pickupLongitude || 77.209),
    };
  }, [formData]);

  const [routeInfo, setRouteInfo] = useState({
    distance: "",
    duration: "",
    path: [],
  });
  const routePath = routeInfo.path || [];

  const normalizeComputedRoute = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse ||
      {};

    const routeData = Array.isArray(data) ? data[0] : data;

    const encodedPolyline =
      routeData?.encodedPolyline ||
      routeData?.polyline ||
      routeData?.overview_polyline?.points ||
      routeData?.routes?.[0]?.polyline?.encodedPolyline ||
      routeData?.routes?.[0]?.overview_polyline?.points ||
      routeData?.routes?.[0]?.polyline ||
      "";

    const distanceText =
      routeData?.distanceText ||
      routeData?.distance ||
      routeData?.routes?.[0]?.distanceText ||
      routeData?.routes?.[0]?.legs?.[0]?.distance?.text ||
      "";

    const durationText =
      routeData?.durationText ||
      routeData?.duration ||
      routeData?.routes?.[0]?.durationText ||
      routeData?.routes?.[0]?.legs?.[0]?.duration?.text ||
      "";

    const routePoints =
      routeData?.route ||
      routeData?.path ||
      routeData?.points ||
      routeData?.coordinates ||
      routeData?.routes?.[0]?.path ||
      routeData?.routes?.[0]?.points ||
      [];

    let finalPath = [];

    if (
      encodedPolyline &&
      window.google?.maps?.geometry?.encoding?.decodePath
    ) {
      finalPath = window.google.maps.geometry.encoding
        .decodePath(encodedPolyline)
        .map((point) => ({
          lat: point.lat(),
          lng: point.lng(),
        }));
    } else if (Array.isArray(routePoints)) {
      finalPath = routePoints
        .map((point) => {
          const lat =
            point?.lat ||
            point?.latitude ||
            point?.location?.lat ||
            point?.location?.latitude;

          const lng =
            point?.lng ||
            point?.longitude ||
            point?.location?.lng ||
            point?.location?.longitude;

          return {
            lat: Number(lat),
            lng: Number(lng),
          };
        })
        .filter(Boolean);
    }

    return {
      path: finalPath,
      distanceText,
      durationText,
    };
  };

  const dropLocation = useMemo(() => {
    return {
      lat: Number(formData?.dropLatitude || pickupLocation.lat + 0.018),
      lng: Number(formData?.dropLongitude || pickupLocation.lng + 0.018),
    };
  }, [formData, pickupLocation]);

  const loadGoogleMap = () => {
    const googleKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!googleKey) {
      console.error("Google Maps API key missing in .env");
      return;
    }

    if (window.google?.maps) {
      initMap();
      return;
    }

    const existingScript = document.getElementById("google-maps-script");

    if (existingScript) {
      existingScript.addEventListener("load", initMap);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = initMap;

    document.body.appendChild(script);
  };

  const initMap = () => {
    if (!mapRef.current || !window.google?.maps) return;

    if (!googleMapRef.current) {
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: pickupLocation,
        zoom: 14,
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: true,
        streetViewControl: false,
        mapTypeControl: false,
      });
    }

    renderMap();
  };

  const clearMapItems = () => {
    if (pickupMarkerRef.current) pickupMarkerRef.current.setMap(null);
    if (dropMarkerRef.current) dropMarkerRef.current.setMap(null);
    if (routeLineRef.current) routeLineRef.current.setMap(null);
  };

  const hideInfoWindowCloseButton = (infoWindow) => {
    infoWindow.addListener("domready", () => {
      const closeButtons = document.querySelectorAll(".gm-ui-hover-effect");

      closeButtons.forEach((btn) => {
        btn.style.display = "none";
      });
    });
  };
  const renderMap = () => {
    if (!window.google?.maps || !googleMapRef.current) return;

    const map = googleMapRef.current;

    clearMapItems();

    pickupMarkerRef.current = new window.google.maps.Marker({
      map,
      position: pickupLocation,
      label: {
        text: "P",
        color: "#ffffff",
        fontWeight: "700",
      },
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 13,
        fillColor: "#2563eb",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 4,
      },
    });

    const pickupInfoWindow = new window.google.maps.InfoWindow({
      content: `
    <div style="
      max-width:220px;
      font-family:Arial,sans-serif;
      padding:0 10px 10px 10px;
      margin:0;
    ">
      <div style="
        font-size:15px;
        font-weight:700;
        margin:0 0 2px 0;
        line-height:18px;
        color:#111827;
      ">
        Pickup Address
      </div>

      <div style="
        font-size:12px;
        line-height:17px;
        color:#4b5563;
        word-break:break-word;
        margin:0;
        padding:0;
      ">
        ${
          formData?.pickupAddress ||
          formData?.pickup_location ||
          formData?.pickup ||
          "Pickup Location"
        }
      </div>
    </div>
  `,
    });

    pickupMarkerRef.current.addListener("mouseover", () => {
      pickupInfoWindow.open(map, pickupMarkerRef.current);
    });

    pickupMarkerRef.current.addListener("mouseout", () => {
      pickupInfoWindow.close();
    });
    hideInfoWindowCloseButton(pickupInfoWindow);

    dropMarkerRef.current = new window.google.maps.Marker({
      map,
      position: dropLocation,
      label: {
        text: "D",
        color: "#ffffff",
        fontWeight: "700",
      },
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 13,
        fillColor: "#16a34a",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 4,
      },
    });

    const dropInfoWindow = new window.google.maps.InfoWindow({
      content: `
    <div style="
      max-width:220px;
      font-family:Arial,sans-serif;
      padding:0 10px 10px 10px;
      margin:0;
    ">
      <div style="
        font-size:15px;
        font-weight:700;
        margin:0 0 2px 0;
        line-height:18px;
        color:#111827;
      ">
        Drop Address
      </div>

      <div style="
        font-size:12px;
        line-height:17px;
        color:#4b5563;
        word-break:break-word;
        margin:0;
        padding:0;
      ">
        ${
          formData?.dropAddress ||
          formData?.drop_location ||
          formData?.drop ||
          "Drop Location"
        }
      </div>
    </div>
  `,
    });
    dropMarkerRef.current.addListener("mouseover", () => {
      dropInfoWindow.open(map, dropMarkerRef.current);
    });

    dropMarkerRef.current.addListener("mouseout", () => {
      dropInfoWindow.close();
    });
    hideInfoWindowCloseButton(dropInfoWindow);
    const finalRoutePath =
      routePath && routePath.length > 1
        ? routePath
        : [pickupLocation, dropLocation];

    routeLineRef.current = new window.google.maps.Polyline({
      map,
      path: finalRoutePath,
      geodesic: true,
      strokeColor: "#2563eb",
      strokeOpacity: 0.95,
      strokeWeight: 5,
    });

    const bounds = new window.google.maps.LatLngBounds();

    bounds.extend(pickupLocation);
    bounds.extend(dropLocation);

    if (routePath?.length > 1) {
      routePath.forEach((point) => {
        bounds.extend(point);
      });
    }

    map.fitBounds(bounds);

    setTimeout(() => {
      if (map.getZoom() > 15) {
        map.setZoom(15);
      }
    }, 300);
  };

  useEffect(() => {
    loadGoogleMap();
  }, []);

  useEffect(() => {
    if (window.google?.maps && googleMapRef.current) {
      renderMap();
    }
  }, [pickupLocation, dropLocation, routePath]);

  useEffect(() => {
    const fetchRouteAndFareDetails = async () => {
      const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");

      const customerId =
        userCode ||
        userID ||
        formData?.userCode ||
        formData?.userId ||
        formData?.userid ||
        sessionUser?.userCode ||
        sessionUser?.userid ||
        sessionUser?.userId ||
        "";

      const originLatitude =
        formData?.pickupLatitude ||
        formData?.originLatitude ||
        formData?.pickupLat ||
        formData?.fromLat ||
        formData?.pickupLocation?.lat ||
        formData?.pickupLocation?.latitude ||
        "";

      const originLongitude =
        formData?.pickupLongitude ||
        formData?.originLongitude ||
        formData?.pickupLng ||
        formData?.fromLng ||
        formData?.pickupLocation?.lng ||
        formData?.pickupLocation?.longitude ||
        "";

      const destinationLatitude =
        formData?.dropLatitude ||
        formData?.destinationLatitude ||
        formData?.dropLat ||
        formData?.toLat ||
        formData?.dropLocation?.lat ||
        formData?.dropLocation?.latitude ||
        "";

      const destinationLongitude =
        formData?.dropLongitude ||
        formData?.destinationLongitude ||
        formData?.dropLng ||
        formData?.toLng ||
        formData?.dropLocation?.lng ||
        formData?.dropLocation?.longitude ||
        "";

      if (!customerId) {
        return;
      }

      if (
        !originLatitude ||
        !originLongitude ||
        !destinationLatitude ||
        !destinationLongitude
      ) {
        return;
      }

      const routeRes = await computeDistanceRouteApi({
        originLatitude,
        originLongitude,
        destinationLatitude,
        destinationLongitude,
        customerId,
      });

      if (!routeRes || routeRes?.responseCode !== "00") {
        return;
      }

      const normalizedRoute = normalizeComputedRoute(routeRes);

      setRouteInfo({
        distance: normalizedRoute.distanceText || "",
        duration: normalizedRoute.durationText || "",
        path:
          normalizedRoute.path.length > 1
            ? normalizedRoute.path
            : [
                {
                  lat: Number(originLatitude),
                  lng: Number(originLongitude),
                },
                {
                  lat: Number(destinationLatitude),
                  lng: Number(destinationLongitude),
                },
              ],
      });

      const fareRes = await getFaresApi({
        customerId,
      });

      if (fareRes?.responseCode === "00" && Array.isArray(fareRes?.data)) {
        const fareMap = {
          1: "twoWheelerFare",
          2: "threeWheelerFare",
          3: "fourWheelerFare",
          4: "miniTruckFare",
        };

        const updatedFareData = {
          twoWheelerFare: {},
          threeWheelerFare: {},
          fourWheelerFare: {},
          miniTruckFare: {},
        };

        fareRes.data.forEach((item) => {
          const key = fareMap[item.vehicle_id];

          if (key) {
            updatedFareData[key] = {
              fare: Number(item.estimated_fare || 0),
              fareId: item.pk_fare_id,
              vehicleId: item.vehicle_id,
            };
          }
        });

        setFareData(updatedFareData);
      }
    };

    fetchRouteAndFareDetails();
  }, [
    formData?.customerId,
    formData?.userId,
    formData?.customer_id,
    formData?.senderMobile,

    formData?.pickupLatitude,
    formData?.originLatitude,
    formData?.pickupLat,
    formData?.fromLat,
    formData?.pickupLocation?.lat,
    formData?.pickupLocation?.latitude,

    formData?.pickupLongitude,
    formData?.originLongitude,
    formData?.pickupLng,
    formData?.fromLng,
    formData?.pickupLocation?.lng,
    formData?.pickupLocation?.longitude,

    formData?.dropLatitude,
    formData?.destinationLatitude,
    formData?.dropLat,
    formData?.toLat,
    formData?.dropLocation?.lat,
    formData?.dropLocation?.latitude,

    formData?.dropLongitude,
    formData?.destinationLongitude,
    formData?.dropLng,
    formData?.toLng,
    formData?.dropLocation?.lng,
    formData?.dropLocation?.longitude,
  ]);

  const faresLoading = computeDistanceRouteLoading || getFaresLoading;

  const vehicles = useMemo(
    () => [
      {
        name: "Two Wheeler",
        img: bike,
        price: fareData.twoWheelerFare?.fare || 0,
        fareId: fareData.twoWheelerFare?.fareId,
        vehicleId: fareData.twoWheelerFare?.vehicleId,
        toll: 0,
        detail: "",
        best: "Best for small parcels and quick delivery",
      },
      {
        name: "Three Wheeler",
        img: auto,
        price: fareData.threeWheelerFare?.fare || 0,
        fareId: fareData.threeWheelerFare?.fareId,
        vehicleId: fareData.threeWheelerFare?.vehicleId,
        toll: 0,
        detail: "",
        best: "Best for medium goods and boxes",
      },
      {
        name: "Four Wheeler",
        img: car,
        price: fareData.fourWheelerFare?.fare || 0,
        fareId: fareData.fourWheelerFare?.fareId,
        vehicleId: fareData.fourWheelerFare?.vehicleId,
        toll: 0,
        detail: "",
        best: "Best for safe covered delivery",
      },
      {
        name: "Pickup Truck",
        img: truck,
        price: fareData.miniTruckFare?.fare || 0,
        fareId: fareData.miniTruckFare?.fareId,
        vehicleId: fareData.miniTruckFare?.vehicleId,
        toll: 0,
        detail: "",
        best: "Best for heavy and commercial goods",
      },
    ],
    [
      bike,
      auto,
      car,
      truck,
      fareData.twoWheelerFare,
      fareData.threeWheelerFare,
      fareData.fourWheelerFare,
      fareData.miniTruckFare,
    ],
  );

  const normalizeVehicleName = (vehicle = "") => {
    const value = String(vehicle).trim().toLowerCase();

    if (
      value.includes("two") ||
      value.includes("2 wheeler") ||
      value === "2" ||
      value.includes("bike")
    ) {
      return "Two Wheeler";
    }

    if (
      value.includes("three") ||
      value.includes("3 wheeler") ||
      value === "3" ||
      value.includes("auto")
    ) {
      return "Three Wheeler";
    }

    if (
      value.includes("four") ||
      value.includes("4 wheeler") ||
      value === "4" ||
      value.includes("eeco") ||
      value.includes("car")
    ) {
      return "Four Wheeler";
    }

    if (
      value.includes("mini") ||
      value.includes("truck") ||
      value.includes("pickup")
    ) {
      return "Pickup Truck";
    }

    return "Two Wheeler";
  };

  const orderedVehicles = useMemo(() => {
    const selectedName = normalizeVehicleName(
      selectedVehicle || sessionStorage.getItem("selectedVehicle") || "",
    );

    const selected = vehicles.find((vehicle) => vehicle.name === selectedName);
    const others = vehicles.filter((vehicle) => vehicle.name !== selectedName);

    return selected ? [selected, ...others] : vehicles;
  }, [selectedVehicle, vehicles]);

  useEffect(() => {
    setActiveVehicle(orderedVehicles[0]?.name || "Two Wheeler");
  }, [orderedVehicles]);

  const selectedVehicleData =
    orderedVehicles.find((vehicle) => vehicle.name === activeVehicle) ||
    orderedVehicles[0];

  const fare = useMemo(() => {
    const tripFare = Number(selectedVehicleData?.price || 0);
    const tollCharges = Number(selectedVehicleData?.toll || 0);
    const netFare = tripFare + tollCharges;

    return {
      tripFare,
      tollCharges,
      netFare,
      amountPayable: netFare,
    };
  }, [selectedVehicleData]);

  useEffect(() => {
    setSelectedFare?.(fare.amountPayable);
  }, [fare.amountPayable, setSelectedFare]);

  const handleBookParcel = async () => {
    const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");

    const customerId =
      userCode ||
      userID ||
      formData?.userCode ||
      formData?.userId ||
      sessionUser?.userCode ||
      sessionUser?.userId ||
      "";

    const response = await bookParcelApi({
      vehilceId: selectedVehicleData?.vehicleId || "",
      fare: selectedVehicleData?.price || 0,
      fareId: selectedVehicleData?.fareId || "",

      pickUpTime:
        formData?.scheduleSlotTime ||
        formData?.scheduleSlotLabel ||
        formData?.scheduleTime ||
        "",

      customerId,

      senderName: formData?.senderName || "",
      senderNumber: formData?.senderMobile || "",

      receiverName: formData?.receiverName || "",
      receiverNumber: formData?.receiverMobile || "",
    });

    console.log("Book Parcel Response:", response);

    if (response?.responseCode === "00") {
      const vehicleMeta = getVehicleMeta(selectedVehicleData?.vehicleId);

      setSelectedVehicleMeta({
        weight: vehicleMeta.weight,
        dimension: vehicleMeta.dimension,
      });

      onNext?.({
        parcelId:
          response?.data?.orderId ||
          response?.data?.parcelId ||
          response?.data?.bookingId ||
          "",
      });
    }
  };

  const [vehicleDetails, setVehicleDetails] = useState([]);

  useEffect(() => {
    const fetchVehicleDetails = async () => {
      const response = await getAllVehicleDetailsApi({
        userId: "",
      });

      console.log("Vehicle API Data", response?.data);

      if (response?.responseCode === "00") {
        setVehicleDetails(response?.data || []);
      }
    };

    fetchVehicleDetails();
  }, []);

  const getVehicleMeta = (vehicleId) => {
    const vehicle = vehicleDetails.find(
      (item) => Number(item.pk_vehicle_id) === Number(vehicleId),
    );

    return {
      weight: vehicle?.weight || "-",
      dimension: vehicle?.dimensions || "-",
    };
  };
  const handleVehicleSelect = (vehicleName) => {
    setActiveVehicle(vehicleName);

    sessionStorage.setItem("selectedVehicle", vehicleName);

    if (setSelectedVehicle) {
      setSelectedVehicle(vehicleName);
    }
  };

  return (
    <section className="w-full px-3 pb-6 pt-8 sm:px-4 md:px-6 md:pt-12 lg:px-0 lg:pt-0">
      <div className="mx-auto mt-4 grid w-full max-w-[1020px] overflow-hidden rounded-2xl border border-white/80 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.16)] sm:rounded-[26px] lg:mt-[120px] lg:grid-cols-[57%_43%]">
        <VehicleFareLeftPanel
          onBack={onBack}
          formData={formData}
          selectedVehicleData={selectedVehicleData}
          fare={fare}
          payAt={payAt}
          setPayAt={setPayAt}
          showPayment={showPayment}
          handleBookParcel={handleBookParcel}
        />

        <VehicleRecommendationPanel
          mapRef={mapRef}
           vehicles={vehicles}
          orderedVehicles={orderedVehicles}
          activeVehicle={activeVehicle}
          handleVehicleSelect={handleVehicleSelect}
          getVehicleMeta={getVehicleMeta}
        />
      </div>
    </section>
  );
};

export default VehicleFareCard;
